import React, { useState, useEffect } from 'react';
import { Table, Card, Row, Col, Input, Button, Typography, App, Modal, Form, Select, Spin, Flex } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux/appState';
import { User } from '@models/user';
import { makeRequest } from '@helpers/axios-manipulator';
import { ClientAdminProperties } from '../../../../data/models/client-admin-properties';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

const { Title } = Typography;

const ClientAdminPropertiesPage = () => {
    const { message } = App.useApp();
    const user = useSelector((state: RootState) => state.user.user);
    const [properties, setProperties] = useState<ClientAdminProperties[]>([]);
    const [clientMap, setClientMap] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingProperty, setEditingProperty] = useState<ClientAdminProperties | null>(null);
    const [form] = Form.useForm();
    const [searchText, setSearchText] = useState('');
    const [clientOptions, setClientOptions] = useState<any[]>([]);
    const [clientLoading, setClientLoading] = useState(false);
    // Fetch all clients for initial load
    useEffect(() => {
        if (isModalVisible) {
            fetchClients();
        }
    }, [isModalVisible]);

    // Fetch all clients for mapping client_id to name
    useEffect(() => {
        const fetchAllClients = async () => {
            try {
                const response = await makeRequest({
                    origin: window.location.origin,
                    method: 'get',
                    url: '/api-billing-search',
                    headers: {},
                });
                const map: Record<string, string> = {};
                (response || []).forEach((item: any) => {
                    if (item.business_id) {
                        map[item.business_id] = item.display_name || item.contact_email || item.business_id;
                    }
                });
                setClientMap(map);
            } catch (error) {
                setClientMap({});
            }
        };
        fetchAllClients();
    }, []);

    const fetchClients = async (search: string = '') => {
        setClientLoading(true);
        try {
            const response = await makeRequest({
                origin: window.location.origin,
                method: 'get',
                url: '/api-billing-search',
                data: search ? { query: search } : undefined,
                headers: {},
            });
            setClientOptions(
                (response || []).map((item: any) => ({
                    label: item.display_name || item.contact_email || item.business_id,
                    value: item.business_id,
                    email: item.contact_email,
                }))
            );
        } catch (error) {
            setClientOptions([]);
        } finally {
            setClientLoading(false);
        }
    };

    const isRootUser = (user: User | null) => {
        if (!user) return false;
        return user.contract_id === 0 || user.contract_id === '0';
    };

    useEffect(() => {
        if (isRootUser(user)) {
            fetchProperties();
        }
    }, [user]);

    const fetchProperties = async (clientId: string = '') => {
        setLoading(true);
        try {
            const url = clientId ? `/api/client-admin-properties/${clientId}` : '/api/client-admin-properties';
            const response = await makeRequest({
                origin: window.location.origin,
                method: 'get',
                url: url,
                headers: {},
            });
            setProperties(response || []);
        } catch (error) {
            message.error('Failed to fetch properties.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value: string) => {
        setSearchText(value);
        if (!value) {
            fetchProperties();
            return;
        }
        // Try to find client_id by name or ID
        let clientId = value;
        const foundId = Object.keys(clientMap).find(
            key => clientMap[key].toLowerCase().includes(value.toLowerCase()) || key.toLowerCase().includes(value.toLowerCase())
        );
        if (foundId) {
            clientId = foundId;
        }
        // If searching by prop_code, filter locally
        const filtered = properties.filter(
            prop =>
                prop.client_id === clientId ||
                (clientMap[prop.client_id] && clientMap[prop.client_id].toLowerCase().includes(value.toLowerCase())) ||
                prop.prop_code.toLowerCase().includes(value.toLowerCase())
        );
        if (filtered.length > 0 || value) {
            setProperties(filtered);
        } else {
            fetchProperties(clientId);
        }
    };

    const showModal = (property: ClientAdminProperties | null = null) => {
        setEditingProperty(property);
        if (property) {
            form.setFieldsValue(property);
        } else {
            form.setFieldsValue({ client_id: '', prop_code: '', prop_value: '' });
        }
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setEditingProperty(null);
        form.resetFields();
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            if (editingProperty) {
                await makeRequest({
                    origin: window.location.origin,
                    method: 'put',
                    url: `/api/client-admin-properties/${editingProperty.id}`,
                    data: values,
                    headers: { 'Content-Type': 'application/json' },
                });
                message.success('Property updated successfully');
            } else {
                await makeRequest({
                    origin: window.location.origin,
                    method: 'post',
                    url: '/api/client-admin-properties',
                    data: values,
                    headers: { 'Content-Type': 'application/json' },
                });
                message.success('Property created successfully');
            }
            fetchProperties(searchText);
            handleCancel();
        } catch (error) {
            message.error('Failed to save property.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await makeRequest({
                origin: window.location.origin,
                method: 'delete',
                url: `/api/client-admin-properties/${id}`,
                headers: {},
            });
            message.success('Property deleted successfully');
            fetchProperties(searchText);
        } catch (error) {
            message.error('Failed to delete property.');
        }
    };

    const columns = [
        {
            title: 'Client Name',
            key: 'client_name',
            render: (_: any, record: ClientAdminProperties) => clientMap[record.client_id] || record.client_id,
        },
        { title: 'Client ID', dataIndex: 'client_id', key: 'client_id' },
        { title: 'Property Code', dataIndex: 'prop_code', key: 'prop_code' },
        { title: 'Property Value', dataIndex: 'prop_value', key: 'prop_value' },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: ClientAdminProperties) => (
                <span>
                    <Button icon={<EditOutlined />} onClick={() => showModal(record)} style={{ marginRight: 8 }}>
                        Edit
                    </Button>
                    <Button icon={<DeleteOutlined />} onClick={() => handleDelete(Number(record.id))} danger>
                        Delete
                    </Button>
                </span>
            ),
        },
    ];

    if (!isRootUser(user)) {
        return <p>You do not have permission to view this page.</p>;
    }

    return (
        <Card 
            title={
                <Flex align="center" gap={16}>
                    <Title level={4} style={{ margin: 0 }}>Client Admin Properties</Title>
                    <Input
                        placeholder="Search by Client ID, Name, or Property Code"
                        value={searchText}
                        onChange={e => handleSearch(e.target.value)}
                        style={{ width: 400 }}
                        allowClear
                    />
                </Flex>
            }
            extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
                    Add Property
                </Button>
            }
        >

            <Table
                columns={columns}
                dataSource={properties}
                loading={loading}
                rowKey="id"
            />

            <Modal
                title={editingProperty ? 'Edit Property' : 'Add Property'}
                open={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                confirmLoading={loading}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="client_id" label="Client" rules={[{ required: true }]}> 
                        <Select
                            showSearch
                            placeholder="Search and select a client"
                            filterOption={false}
                            onSearch={fetchClients}
                            notFoundContent={clientLoading ? <Spin size="small" /> : null}
                            options={clientOptions}
                            disabled={!!editingProperty}
                        />
                    </Form.Item>
                    <Form.Item name="prop_code" label="Property Code" rules={[{ required: true }]}> 
                        <Input disabled={!!editingProperty}/>
                    </Form.Item>
                    <Form.Item name="prop_value" label="Property Value" rules={[{ required: true }]}> 
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default ClientAdminPropertiesPage;
